import CustomAmplitudeClient from "./custom-amplitude-client.8d2468912b6f.js";
import {
  InsuranceSelectorChangedEvent,
  InsuranceSelectorNetworkChosenEventType,
  InsuranceSelectorDialogNetworkSelectedEventType,
  InsuranceSelectorDialogMemberIdConfirmedEventType,
  InsuranceSelectorDialogNoInsuranceSelectedEventType,
  InsuranceSelectorDialogNetworkResetEventType,
  OpenInsuranceSelectorEvent,
} from "./insurance-selector-events.87d20ca02104.js";
import { FloatingLabel } from "./floating-label.895f0e243516.js";

/**
 * Custom element <insurance-selector> for displaying and selecting insurance networks.
 *
 * Expects child elements:
 * - .tqc-click-target: The clickable area to open dialog
 * - #insurance-selector-toggle: Text element displaying network name
 * - #insurance-selector-member-id .tqc-insurance-member-id-value: Member ID display (optional)
 * - input[name=network_config][type=hidden]: Hidden input storing selected network ID
 *
 * Events emitted:
 * - "tqc:insurance-selector:open" - When user clicks to open insurance selector dialog
 *   detail: { navMethod: "insurance-selector" }
 * - "tqc:insurance-selector:changed" - When network selection changes
 *   detail: { fullName, networkConfigId, coverageType, memberId?, networkDisplayName? }
 *
 * Events listened:
 * - "tqc:insurance-selector:network-chosen" - Network selected without member ID
 *   detail: { fullName, networkConfigId, coverageType }
 * - "tqc-network-selected" - Network selected in dialog (dispatched by server via HTMX HX-Trigger)
 *   detail: { fullName, networkConfigId }
 * - "tqc-selected-no-insurance" - No network/cash option selected
 *   detail: { fullName, networkConfigId }
 * - "tqc-network-reset" - Network selection reset
 *   detail: { fullName, networkConfigId }
 * - "tqc-benefits-confirmed" - Member ID confirmed (dispatched by server via HTMX HX-Trigger)
 *   detail: { networkDisplayName, networkConfigId, memberId }
 *
 * DOM mutations:
 * - Updates #insurance-selector-toggle innerText with selected network name
 * - Updates .tqc-insurance-member-id-value innerText with member ID
 * - Updates input[name=network_config] value with selected network ID
 * - Adds/removes member-id attribute on <insurance-selector> element
 */
class InsuranceSelector extends HTMLElement {
  constructor() {
    super();

    this.parent = this.getRootNode() || document;
    this.config = JSON.parse(
      this.parent.querySelector("#page-config").textContent,
    );
    if (!this.config) throw new Error("unable to load page config");

    this.elements = {
      toggle: this.querySelector(".tqc-click-target"),
      nameDisplay: this.querySelector("#insurance-selector-toggle"),
      memberIdDisplay: this.querySelector(
        "#insurance-selector-member-id .tqc-insurance-member-id-value",
      ),
      hiddenNetworkInput: this.querySelector(
        "input[name=network_config][type=hidden]",
      ),
    };
    this.amplitudeClient = new CustomAmplitudeClient(this.config.analyticsUrl);
    this.floatingLabel = this.closest(".tqc-floating-label-group")
      ? new FloatingLabel(this)
      : null;

    this.bindToggleEvents();
  }

  connectedCallback() {
    this.parent.addEventListener(
      InsuranceSelectorNetworkChosenEventType,
      this.handleNetworkChosen,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogNoInsuranceSelectedEventType,
      this.handleNetworkChosen,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogNetworkResetEventType,
      this.handleNetworkReset,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogNetworkSelectedEventType,
      this.handleNetworkChosen,
    );
    this.parent.addEventListener(
      InsuranceSelectorDialogMemberIdConfirmedEventType,
      this.handleMemberIdConfirmed,
    );
  }

  disconnectedCallback() {
    this.parent.removeEventListener(
      InsuranceSelectorNetworkChosenEventType,
      this.handleNetworkChosen,
    );
    this.parent.removeEventListener(
      InsuranceSelectorDialogNoInsuranceSelectedEventType,
      this.handleNetworkChosen,
    );
    this.parent.removeEventListener(
      InsuranceSelectorDialogNetworkResetEventType,
      this.handleNetworkReset,
    );
    this.parent.removeEventListener(
      InsuranceSelectorDialogMemberIdConfirmedEventType,
      this.handleMemberIdConfirmed,
    );
  }

  bindToggleEvents() {
    this.elements.toggle.addEventListener("click", this.handleOpen);
    this.addEventListener("keydown", (e) => {
      if (e.key === "Enter" && e.target === this) {
        this.handleOpen();
      }
    });
  }

  handleOpen = () => {
    this.dispatchEvent(
      new OpenInsuranceSelectorEvent(false, {
        navMethod: "insurance-selector",
      }),
    );
  };

  updateNetwork(name, value) {
    this.elements.nameDisplay.innerText = name;
    this.elements.hiddenNetworkInput.value = value;
    this.floatingLabel?.update(true, false);
  }

  resetNetwork(name) {
    this.elements.nameDisplay.innerHTML = `<span class="pv-text-subdued">${name}</span>`;
    this.elements.hiddenNetworkInput.value = "";
    this.floatingLabel?.update(false, false);
  }

  handleNetworkChosen = (e) => {
    this.updateNetwork(e.detail.fullName, e.detail.networkConfigId);
    this.removeAttribute("member-id");
    this.dispatchEvent(new InsuranceSelectorChangedEvent(e.detail));
    this.amplitudeNetworkChosen(e.detail);
  };

  handleNetworkReset = (e) => {
    this.resetNetwork(e.detail.fullName);
    this.removeAttribute("member-id");
    this.dispatchEvent(new InsuranceSelectorChangedEvent(e.detail));
    this.amplitudeNetworkChosen(e.detail);
  };

  // triggered by htmx after successfully submitting the insurance form (uses HX-Trigger response header)
  handleMemberIdConfirmed = (e) => {
    this.setAttribute("member-id", "");
    this.updateNetwork(e.detail.networkDisplayName, e.detail.networkConfigId);
    if (this.elements.memberIdDisplay) {
      this.elements.memberIdDisplay.innerText = e.detail.memberId;
    }
    this.dispatchEvent(new InsuranceSelectorChangedEvent(e.detail));
    this.amplitudeClient.trackEvent("insurance_plan_selected", {
      network_config_id: e.detail.networkConfigId ?? "",
      network_name: e.detail.networkDisplayName ?? "",
      coverage_type: "",
      product_flow: this.config.productFlow,
      search_stage: this.config.searchStage,
    });
  };

  amplitudeNetworkChosen(detail) {
    this.amplitudeClient.trackEvent("insurance_plan_selected", {
      network_config_id: detail.networkConfigId ?? "",
      network_name: detail.fullName ?? "",
      coverage_type: detail.coverageType ?? "",
      product_flow: this.config.productFlow,
      search_stage: this.config.searchStage,
    });
  }
}

customElements.define("insurance-selector", InsuranceSelector);
